import { useEffect, useMemo, useRef, useState } from 'react';
import { bootConfig } from '../data/terminalContent';

interface BootSequenceProps {
  onComplete: () => void;
  skip?: boolean;
}

type BootPhase = 'bios' | 'memory' | 'loading' | 'complete';

const MEMORY_STEP_KB = 16;

export function BootSequence({ onComplete, skip = false }: BootSequenceProps) {
  const [phase, setPhase] = useState<BootPhase>('bios');
  const [memory, setMemory] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableOnComplete = useRef(onComplete);
  stableOnComplete.current = onComplete;

  useEffect(() => {
    if (skip) {
      stableOnComplete.current();
      return () => undefined;
    }

    const timers: number[] = [];

    const biosTimer = window.setTimeout(() => {
      setPhase('memory');
      const memoryTimer = window.setInterval(() => {
        setMemory((current) => {
          const next = Math.min(current + MEMORY_STEP_KB, bootConfig.memoryLimitKb);
          if (next >= bootConfig.memoryLimitKb) {
            window.clearInterval(memoryTimer);
            setPhase('loading');
            const loadTimer = window.setTimeout(() => {
              setPhase('complete');
              const doneTimer = window.setTimeout(() => stableOnComplete.current(), 550);
              timers.push(doneTimer);
            }, 700);
            timers.push(loadTimer);
          }
          return next;
        });
      }, 26);
      timers.push(memoryTimer);
    }, 320);

    timers.push(biosTimer);

    return () => {
      timers.forEach((timer) => {
        window.clearTimeout(timer);
        window.clearInterval(timer);
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  const memoryLine = useMemo(() => {
    const suffix = memory >= bootConfig.memoryLimitKb ? 'OK' : '...';
    return `Memory Test: ${memory.toString().padStart(4, '0')}K ${suffix}`;
  }, [memory]);

  return (
    <div className="boot-sequence" aria-live="polite">
      <p>{bootConfig.biosLine}</p>
      {(phase === 'memory' || phase === 'loading' || phase === 'complete') && <p>{memoryLine}</p>}
      {(phase === 'loading' || phase === 'complete') && <p>{bootConfig.loadingLine}</p>}
    </div>
  );
}
